import React, { useState } from "react";
import classNames from "classnames";
import { HighlightedParts } from "../source_directory/HighlightAttribute";
import {
  hasHiddenHighlight,
  highlightParts,
  truncateHighlightParts,
  visibleLength,
} from "../source_directory/highlightTruncate";

const MAX_BODY_LENGTH = 280;

// `highlightValue` is an Algolia highlight string (_highlightResult.<attr>.value, matches wrapped in
// <mark>): when set it drives truncation (by visible length, never cutting inside a <mark>) and
// rendering via HighlightedParts; when absent the plain-string `body` path is used. The size props
// default to the feed/pitch-inbox appearance.
function BodyTextWithExpand({
  body,
  maxLength = MAX_BODY_LENGTH,
  textClassName = "paragraph-md",
  textStyle,
  toggleClassName = "paragraph-sm",
  highlightValue,
}) {
  const highlighted = typeof highlightValue === "string";

  // Auto-expand when the current query hides a matched term in the truncated-away tail. Derived from
  // the CURRENT highlightValue every render (not a useState initializer, which would freeze at mount
  // and miss query changes on reuse).
  const autoExpand = highlighted && hasHiddenHighlight(highlightValue, maxLength);

  // `override` is the manual toggle; it applies only while the auto-expand condition holds. When a
  // new query flips the condition we drop the stale override so the derived default takes over.
  const [override, setOverride] = useState(null);
  const [conditionKey, setConditionKey] = useState(autoExpand);

  if (conditionKey !== autoExpand) {
    setConditionKey(autoExpand);
    setOverride(null);
  }

  const expanded = override === null ? autoExpand : override;
  const setExpanded = (next) => setOverride(next);

  if (highlighted ? highlightValue === "" : !body) return null;

  const contentLength = highlighted ? visibleLength(highlightValue) : body.length;
  const isTruncated = contentLength > maxLength;

  let content;
  if (highlighted) {
    const parts = expanded ? highlightParts(highlightValue) : truncateHighlightParts(highlightValue, maxLength);
    content = <HighlightedParts parts={parts} />;
  } else {
    content = expanded ? body : body.substring(0, maxLength);
  }

  return (
    <p className={classNames("mb-0 text-secondary", textClassName)} style={{ fontWeight: 400, ...textStyle }}>
      {content}
      {isTruncated && (
        <>
          {!expanded && "... "}
          <span
            role="button"
            tabIndex={0}
            className={classNames("text-primary-60 feed-toggle-link", toggleClassName, { "ms-1": expanded })}
            onClick={() => setExpanded(!expanded)}
            onKeyDown={(e) => e.key === "Enter" && setExpanded(!expanded)}
          >
            {expanded ? "less" : "more"}
          </span>
        </>
      )}
    </p>
  );
}

export default BodyTextWithExpand;
export { MAX_BODY_LENGTH };
